import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  CircularProgress,
  IconButton
} from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { supportedCurrencies } from '../utils/currencyUtils';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';

const SimpleCurrencySelector = ({ iconOnly = false, fullWidth = false, menuPlacement = 'bottom' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { currency, changeCurrency, loading } = useCurrency();
  const { language } = useLanguage();
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCurrencyChange = (currencyCode) => {
    changeCurrency(currencyCode);
    handleClose();
  };

  // Determinar si estamos en modo de selección completa (texto que muestra la moneda actual)
  const isFullTextMode = !iconOnly && fullWidth;
  
  // Elemento que desencadena el menú, diferente según el modo
  const triggerElement = iconOnly ? (
    <CurrencyExchangeIcon 
      onClick={handleClick} 
      sx={{ cursor: 'pointer' }} 
    />
  ) : isFullTextMode ? (
    // Versión de texto completo para el menú
    <Typography 
      variant="body2" 
      onClick={handleClick} 
      sx={{ 
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        '&:hover': {
          textDecoration: 'underline',
        }
      }}
    >
      {supportedCurrencies[currency]?.name || currency}
    </Typography>
  ) : (
    // Versión de botón normal
    <Tooltip title={language === 'es' ? 'Seleccionar Moneda' : 'Select Currency'}>
      <Button
        variant="outlined"
        size="small"
        color="inherit"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CurrencyExchangeIcon />}
        onClick={handleClick}
        sx={{ 
          borderColor: 'rgba(255, 255, 255, 0.5)', 
          color: 'inherit',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.8)',
          }
        }}
      >
        {currency}
      </Button>
    </Tooltip>
  );

  // 17 sep 2026 (pedido de Augusto: "se puede tener una version completa en
  // ingles?"): antes este título se decidía por el idioma del NAVEGADOR
  // (window.navigator.language), no por el idioma elegido en la app -- un
  // cliente que dejó su app en inglés pero tiene el navegador en español
  // (o viceversa) veía este único texto desincronizado del resto de la
  // pantalla. Ahora usa el mismo LanguageContext que toda la app.
  const menuTitle = language === 'es' ? 'Seleccionar Moneda' : 'Select Currency';

  return (
    <Box>
      {triggerElement}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          dense: true
        }}
        PaperProps={{
          elevation: 1,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: menuPlacement === 'bottom' ? 1.5 : 0,
            ml: menuPlacement === 'right' ? 1 : 0,
            minWidth: 200,
            zIndex: 2000
          },
        }}
        anchorOrigin={{
          vertical: menuPlacement === 'bottom' ? 'bottom' : 'center',
          horizontal: menuPlacement === 'bottom' ? 'center' : 'right',
        }}
        transformOrigin={{
          vertical: menuPlacement === 'bottom' ? 'top' : 'center',
          horizontal: menuPlacement === 'bottom' ? 'center' : 'left',
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
          {menuTitle}
        </Typography>
        {Object.keys(supportedCurrencies).map((currencyCode) => (
          <MenuItem 
            key={currencyCode}
            onClick={() => handleCurrencyChange(currencyCode)}
            selected={currency === currencyCode}
            dense
          >
            <ListItemIcon>
              {supportedCurrencies[currencyCode].symbol}
            </ListItemIcon>
            <ListItemText>
              {supportedCurrencies[currencyCode].name}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default SimpleCurrencySelector;
